/**
 * @typedef { import('../time.js').YearMonthPeriod } YearMonthPeriod
 * @typedef { import('../time.js').YearMonthPeriodObject } YearMonthPeriodObject
 * @typedef { import('../time.js').YearMonth } YearMonth
 */

import {parseYearMonth} from '../time.js';
import {convertObjectToYearMonthPeriod} from '../time.js';

//-----------------------------------------------------------------------------

export class CohortReport {
  /**
   * @param {YearMonthPeriod} period
   * @param {Cohort[]} cohorts
   */
  constructor(period, cohorts) {
    this.period = period;
    this.cohorts = cohorts;
  }

  getPeriod() {
    return this.period;
  }

  getCohorts() {
    return this.cohorts;
  }

  /**
   * @param {YearMonth} month 
   * @returns {?Cohort}
   */
  findCohortByMonth(month) {
    for(const cohort of this.cohorts) {
      if(cohort.getYearMonth().isEqual(month)) {
        return cohort;
      }
    }
    return null;
  }
}

/**
 * @typedef {Object} CohortReportObject
 * @property {YearMonthPeriodObject} period
 * @property {CohortObject[]} cohorts
 */

/**
 * @param {CohortReportObject} reportObject 
 * @returns {?CohortReport}
 */
export function convertObjectToCohortReport(reportObject) {
  const period = convertObjectToYearMonthPeriod(reportObject.period);
  if(period === null) {
    return null;
  }

  const cohorts = convertArrayToCohorts(reportObject.cohorts);
  if(cohorts === null) {
    return null;
  }

  return new CohortReport(period, cohorts);
}

/**
 * @param {CohortReport} report
 * @returns {string}
 */
export function convertCohortReportToString(report) {
  let str = "cohort;player_id\n";

  for(const cohort of report.getCohorts()) {
    const cohortName = cohort.getName();
    for(const playerId of cohort.playerIds) {
      str += cohortName + ';' + playerId + "\n";
    }
  }
  return str
}

//-----------------------------------------------------------------------------

export class Cohort {
  /**
   * @param {YearMonth} yearMonth
   * @param {number[]} playerIds
   */
  constructor(yearMonth, playerIds) {
    /** @type {YearMonth} */
    this.yearMonth = yearMonth;

    /** @type {number[]} */
    this.playerIds = playerIds;
  }

  getName() {
    return this.yearMonth.toString();
  }

  getYearMonth() {
    return this.yearMonth;
  }

  getPlayerIds() {
    return this.playerIds;
  }
}

/**
 * @typedef {Object} CohortObject
 * @property {string} month
 * @property {number[]} playerIds
 */

/**
 * @param {CohortObject} cohortObj
 * @returns {?Cohort}
 */
export function convertObjectToCohort(cohortObj) {
  const ym = parseYearMonth(cohortObj.month);
  if(ym === null) {
    return null;
  }

  return new Cohort(ym, cohortObj.playerIds);
}

/**
 * @param {CohortObject[]} cohortObjects
 * @returns {Cohort[]}
 */
export function convertArrayToCohorts(cohortObjects) {
  const cohorts = [];

  for(const cohortObj of cohortObjects) {
    const cohort = convertObjectToCohort(cohortObj);
    cohorts.push(cohort);
  }

  return cohorts;
}

//-----------------------------------------------------------------------------
